class TrimEdgeRule {
  constructor(label) {
    this._label = label;
  }

  getBackendObjects() {
    return this._label;
  }
}

export class AreaBorderLabelState {
  constructor(rawArea, allLabels, defaultLabels) {
    this._allLabels = allLabels;
    this._defaultLabels = defaultLabels;
    this._borderLabelSets = new Map();

    this.setState(rawArea);
  }

  _checkBorderRange(borderIndex) {
    if (!this._borderLabelSets.has(borderIndex)) {
      throw new RangeError('Border index does not exist: ' + borderIndex);
    }
  }

  get allLabels() {
    return this._allLabels;
  }

  addLabel(borderIndex, label) {
    this._checkBorderRange(borderIndex);
    this._borderLabelSets.get(borderIndex).set(label, new TrimEdgeRule(label));
  }

  removeLabel(borderIndex, label) {
    this._checkBorderRange(borderIndex);
    this._borderLabelSets.get(borderIndex).delete(label);
  }

  getLabelsForBorder(borderIndex) {
    this._checkBorderRange(borderIndex);
    return this._borderLabelSets.get(borderIndex);
  }

  doesBorderHaveLabel(borderIndex, label) {
    this._checkBorderRange(borderIndex);
    return this._borderLabelSets.get(borderIndex).has(label);
  }

  setState(rawArea) {
    if (!rawArea) {
      return;
    }

    if (rawArea.data.user_trims_selection) {
      this._borderLabelSets = AreaBorderLabelState._parseBackendObjects(rawArea);
    } else {
      this._borderLabelSets = AreaBorderLabelState._defaultState(rawArea, this._defaultLabels);
    }
  }

  clearState(rawArea) {
    if (!rawArea) {
      return;
    }

    delete rawArea.data.user_trims_selection;
    this._borderLabelSets = new Map();
  }

  getBackendObjects() {
    const result = {};

    for (const [borderIndex, labelMap] of this._borderLabelSets) {
      const labels = [];
      for (const [, trimEdgeRule] of labelMap) {
        labels.push(trimEdgeRule.getBackendObjects());
      }

      result[borderIndex] = labels;
    }

    return result;
  }

  static _parseBackendObjects(rawArea) {
    if (!rawArea.data.user_trims_selection) {
      throw new Error("Missing 'user_trims_selection' in rawArea");
    }

    const edge_labels = rawArea.data.user_trims_selection;

    const borderLabelSets = new Map();
    for (let borderIndex = 0; borderIndex < rawArea.data.points.length; borderIndex++) {
      const trimEdgeRules = new Map();

      if (edge_labels[borderIndex.toString()]) {
        for (const label of edge_labels[borderIndex.toString()]) {
          const trimEdgeRule = new TrimEdgeRule(label);
          trimEdgeRules.set(label, trimEdgeRule);
        }
      }

      borderLabelSets.set(borderIndex, trimEdgeRules);
    }

    return borderLabelSets;
  }

  static _defaultState(rawArea, defaultLabels) {
    const borderLabelSets = new Map();

    for (let borderIndex = 0; borderIndex < rawArea.data.points.length; borderIndex++) {
      const trimEdgeRules = new Map();

      // Set the default labels for this border
      const boardsDirection = AreaBorderLabelState._getBoardsDirection(rawArea, borderIndex);
      if (boardsDirection) {
        const labels = new Set(defaultLabels[boardsDirection]);
        for (const label of labels) {
          trimEdgeRules.set(label, new TrimEdgeRule(label));
        }
      }

      borderLabelSets.set(borderIndex, trimEdgeRules);
    }

    return borderLabelSets;
  }

  static _getBoardsDirection(rawArea, borderIndex) {
    if (!rawArea.edge_positions || !rawArea.edge_positions.outer) {
      return null;
    }

    const outerEdges = rawArea.edge_positions.outer;
    if (!outerEdges[borderIndex]) {
      return null;
    }

    if (!outerEdges[borderIndex].board_referenced) {
      return null;
    }

    return outerEdges[borderIndex].board_referenced;
  }
}
